




















































import {
  Component, Prop, Ref, Vue, Watch,
} from 'vue-property-decorator';
import VueSmartSliderMarks from './VueSmartSliderMarks.vue';
import VueSmartSliderPips from './VueSmartSliderPips.vue';
import VueSmartSliderProcess from './VueSmartSliderProcess.vue';
import { SliderMarker } from './types/SliderTypes';

@Component({ components: { VueSmartSliderMarks, VueSmartSliderPips, VueSmartSliderProcess } })
export default class VueSmartSlider extends Vue {
  name = 'VueSmartSlider';

  @Prop({ type: Number, required: false, default: 0 }) readonly min!: number;

  @Prop({ type: Number, required: false, default: 0 }) readonly max!: number;

  @Prop({ type: Number, required: false, default: 10 }) readonly width!: number;

  @Prop({ type: Boolean, required: false, default: false }) readonly disabled!: boolean;

  @Prop({ type: Array, required: false, default: () => [] }) readonly marker!: Array<SliderMarker>;

  @Prop({ type: Boolean, required: false, default: true }) readonly propProcess!: boolean;

  @Prop({ type: Number, required: false, default: 10 }) readonly propDefaultValue!: number;

  @Prop({ type: String, required: false, default: '#ccc' }) readonly propRailColor!: string;

  @Prop({ type: Number, required: false, default: 18 }) readonly propRailHeight!: number;

  @Prop({ type: Boolean, required: false, default: false }) readonly propLabel!: boolean;

  @Prop({ type: Boolean, required: false, default: false }) readonly propFloatingValues!: boolean;

  @Prop({
    type: String,
    required: false,
    default: 'left 250ms ease-in, width 250ms ease-in',
  }) readonly propTransition!: string;

  @Prop({ type: Array, required: false, default: () => [] }) readonly propMarks!: [];

  @Prop({ type: Number, required: false, default: undefined }) readonly propValue!: number;

  @Prop({ type: Boolean, required: false, default: false }) readonly propFixedPips!: boolean;

  @Ref('VueSmartRail') readonly rail!: HTMLElement;

  @Ref('VueSmartControl') readonly control!: HTMLElement;

  transition = '';

  railWidth = 0;

  isDragging = false;

  handlePosition = {
    left: 0,
    right: 0,
  };

  debug = {
    cursorPosition: 0,
    cursorPositionRelativeToElement: 0,
    cursorPercentageToElement: 0,
    value: 0,
  };

  options = {
    width: 200,
  };

  isMounted = false;

  value = 50;

  @Watch('disabled')
  onDisabledChanged(newValue: boolean): void {
    if (newValue === true) {
      this.isDragging = false;
    }
  }

  @Watch('value')
  onValueChanged(newValue: number): void {
    this.$emit('on-value-changed', newValue);
  }

  @Watch('propValue')
  onPropValueChanged(newValue: number): void {
    this.moveControl(newValue);
  }

  @Watch('isDragging')
  onIsDraggingChanged(newValue: boolean) {
    document.body.style.cursor = newValue ? 'pointer' : 'default';
  }

  mounted(): void {
    this.bindEvents();
    this.$forceUpdate();
  }

  created(): void {
    this.$nextTick(() => {
      this.onResize();

      window.addEventListener('resize', this.onResize);
    });
  }

  destroyed(): void {
    window.removeEventListener('resize', this.onResize);

    document.removeEventListener('mousemove', this.onContainerMouseMove);
    document.removeEventListener('mouseup', this.onControlMouseUp);
    document.removeEventListener('touchmove', this.onContainerMouseMove);
    document.removeEventListener('touchend', this.onControlMouseUp);
  }

  /**
   *  gets called to adjust the size of the slider when the window is resized
   */
  onResize(): void {
    if (this.rail) {
      this.railWidth = this.rail.getBoundingClientRect().width;
    }
  }

  bindEvents(): void {
    this.isMounted = true;
    this.value = this.propDefaultValue;

    document.addEventListener('mousemove', this.onContainerMouseMove);
    document.addEventListener('mouseup', this.onControlMouseUp);
    document.addEventListener('touchmove', this.onContainerMouseMove);
    document.addEventListener('touchend', this.onControlMouseUp);

    this.rail.addEventListener('click', this.onRailClick);
  }

  onControlMouseDown(event: Event): void {
    if (this.disabled) return;

    this.isDragging = true;
  }

  onControlMouseUp(event: MouseEvent | TouchEvent): void {
    if (this.disabled) return;

    if (this.isDragging) {
      this.isDragging = false;

      document.body.style.cursor = 'auto';
      this.$emit('on-slider-dragged', this.value);
    }
  }

  onContainerMouseMove(event: MouseEvent | TouchEvent): void {
    if (this.disabled) return;

    if (this.isDragging) {
      const oldValue = this.value;
      const x = event.type === 'touchmove'
        ? (event as TouchEvent).changedTouches[0].clientX
        : (event as MouseEvent).clientX;
      this.transition = '';
      const newValue = this.getValueByPosition(x);

      if (newValue <= this.max && newValue >= this.min) {
        this.$emit('on-slider-dragging',
          !this.propFloatingValues ? oldValue : Math.floor(oldValue),
          !this.propFloatingValues ? newValue : Math.floor(newValue));

        this.moveControl(newValue);
      }
    }
  }

  moveControl(value: number): void {
    this.value = value;
  }

  /**
   * used to calculate the absolute value of the control
   * */
  getValueByPosition(z: number): number {
    const c = this.control.getBoundingClientRect().left;
    const cW = this.control.offsetWidth;
    const cP = c + cW / 2;
    const rW = this.rail.offsetWidth;
    const rL = this.rail.getBoundingClientRect().left;
    const x = rL + cW / 2;
    const a = rW - (cW / 2);
    const zA = z - x;
    const zP = zA / a * 100;

    if (this.marker && this.marker.length) {
      const pips: Array<SliderMarker> = this.marker;
      let value = null;

      pips.forEach((pip: SliderMarker, index) => {
        let nextPip = pip;
        if (index + 1 in pips) {
          nextPip = pips[index + 1];
        }

        if (zP >= this.getMaxPip().percentage) {
          value = this.getMaxPip().value;
        } else if (zP >= pip.percentage && zP <= nextPip.percentage) {
          const increment = (nextPip.value - pip.value) / (nextPip.percentage - pip.percentage);
          value = pip.value + (zP - pip.percentage) * increment;
        }
      });

      value = value < this.getMinPip().value
        ? this.getMinPip().value
        : (value > this.getMaxPip().value
          ? this.getMaxPip().value
          : value
        );

      if (this.propFixedPips) {
        const closest = pips.reduce(
          (prev, curr) => (
            Math.abs(curr.value - value) < Math.abs(prev.value - value)
              ? curr
              : prev
          ),
        );

        if (closest !== undefined) {
          value = closest.value;
        }
      }

      return this.propFloatingValues ? value : Math.round(value);
    }

    let value: number = zP / 100 * this.max;
    value = value < this.min ? this.min : (value > this.max ? this.max : value);

    return this.propFloatingValues ? value : Math.round(value);
  }

  /**
   * returns the absolute left position of the slider control
   */
  get left(): number {
    let leftPosition = 0;

    if (this.isMounted) {
      if (this.marker && this.marker.length) {
        const pips = this.marker;
        const { value } = this;

        pips.forEach((pip, index) => {
          let nextPip: SliderMarker = pip;
          if (index + 1 in pips) {
            nextPip = pips[index + 1];
          }

          if (value >= pip.value && value <= nextPip.value) {
            let increment:
            number = (nextPip.value - pip.value) / (nextPip.percentage - pip.percentage);

            if (Number.isNaN(increment)) {
              increment = 1;
            }

            leftPosition = this.railWidth / 100
              * (pip.percentage + (value - pip.value) / increment)
              - (this.control.getBoundingClientRect().width / 2);
          }
        }, this);
      } else {
        leftPosition = this.value
          / this.max
          * this.railWidth
          - (this.control.getBoundingClientRect().width / 2);
      }
    }

    return leftPosition;
  }

  getMinPip(): SliderMarker | undefined {
    return this.marker.reduce(
      (
        prev: SliderMarker,
        current: SliderMarker,
      ) => ((prev.value < current.value) ? prev : current),
    );
  }

  /**
   * gets the maximal pip element from the properties
   */
  getMaxPip(): SliderMarker | null {
    return this.marker.reduce(
      (
        prev: SliderMarker,
        current: SliderMarker,
      ) => ((prev.value > current.value) ? prev : current),
    );
  }

  onRailClick(event: MouseEvent): void {
    if (this.disabled) return;

    this.transition = this.propTransition;
    const value = this.getValueByPosition(event.clientX);

    this.$emit('on-slider-clicked', this.propFloatingValues ? value : Math.floor(value));

    this.moveControl(value);
  }
}
